import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import TenantPicker from '../common/TenantPicker';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { openTenantAdminWorkspace } from '../../utils/tenantAdminWorkspace';

const LAST_TENANT_KEY = 'platform_lastSelectedTenantId';

/**
 * Compact picker bar shown at the top of /platform/* pages.
 * Lets a superadmin pick a tenant and navigate to the same page in that
 * tenant's context (/t/{tenantId}/{currentPageSlug}{currentSearch}).
 * The underlying page components already work for tenant-scoped data,
 * so no per-page refactor is needed.
 *
 * No page title is rendered here — each underlying page keeps its own
 * h1/header. This bar is intentionally compact to sit above the page's
 * native title without visual duplication.
 *
 * Props:
 *   tenantOptional — if true, shows a "leave blank to view all tenants"
 *                    hint so cross-tenant pages (like /platform/users)
 *                    remain usable without selection. Default: false.
 */
const PlatformPageHeader = ({ tenantOptional = false }) => {
  const location = useLocation();

  // Extract the page slug after /platform/ — e.g. "/platform/analytics"
  // → "analytics". This becomes the path appended to /t/{id}/. Nested
  // routes like /platform/rag/collections preserve as "rag/collections".
  const pageSlug = location.pathname.replace(/^\/platform\/?/, '') || 'dashboard';

  const [selectedTenant, setSelectedTenant] = useState(null);
  const [recentTenant, setRecentTenant] = useState(null);

  // Surface the most recently picked tenant as a quick-link without
  // pre-selecting it (so the page doesn't auto-navigate on mount).
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LAST_TENANT_KEY);
      if (raw) setRecentTenant(JSON.parse(raw));
    } catch {
      // Ignore — quick-link is best-effort
    }
  }, []);

  // Preserve query params (?range=7d, ?filter=foo) when crossing
  // platform → tenant context.
  const goToTenant = (tenantId) => {
    openTenantAdminWorkspace(tenantId, pageSlug, location.search || '');
  };

  const handlePick = (tenant) => {
    if (!tenant?.id) return;
    setSelectedTenant(tenant);
    try {
      localStorage.setItem(LAST_TENANT_KEY, JSON.stringify({
        id: tenant.id,
        name: tenant.name,
        slug: tenant.slug,
      }));
    } catch {
      // Ignore — persistence is best-effort
    }
    goToTenant(tenant.id);
  };

  return (
    <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-3">
      <div className="flex items-end gap-3 flex-wrap">
        <div className="flex-1 min-w-[280px] max-w-[480px]">
          <TenantPicker
            selectedTenant={selectedTenant}
            value={selectedTenant?.id || ''}
            onChange={() => {}}
            onTenantSelect={handlePick}
            label="Select Tenant"
            placeholder="Search by name, slug, email, or ID"
            helperText={
              tenantOptional
                ? 'Optional — leave blank to view all tenants'
                : 'Pick a tenant to open this page in their context'
            }
          />
        </div>
        {recentTenant?.id && (!selectedTenant || selectedTenant.id !== recentTenant.id) && (
          <button
            type="button"
            onClick={() => goToTenant(recentTenant.id)}
            className="flex items-center gap-2 text-xs px-3 py-2 mb-1 rounded border border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/50"
          >
            <span>
              Recent: <span className="font-semibold">{recentTenant.name}</span>
              {recentTenant.slug && <span className="opacity-70 ml-1">({recentTenant.slug})</span>}
            </span>
            <ArrowRightIcon className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
};

export default PlatformPageHeader;
